/**
 * Custom renderer for final_result tool
 * Renders markdown content plus any figures, dataframes, values, or files
 *
 * During live streaming, artifacts are resolved via the batch
 * GET /artifacts/resolve endpoint (single round trip).
 * For conversation history, pre-resolved artifacts are passed in directly.
 */
import { type FC, useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { Loader2 } from "lucide-react";
import remarkGfm from "remark-gfm";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { BlobFileMessage } from "~/components/assistant/messages/BlobFileMessage";
import { DataValueMessage } from "~/components/assistant/messages/DataValueMessage";
import { DataframeMessage } from "~/components/assistant/messages/DataframeMessage";
import { FigureMessage } from "~/components/assistant/messages/FigureMessage";
import { TextFileMessage } from "~/components/assistant/messages/TextFileMessage";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface EnrichedArtifact {
  artifact_id: string;
  artifact_name?: string;
  type: "figure" | "dataframe" | "value" | "text_file" | "blob_file";
  content: any;
  summary?: string;
  code?: string;
}

const ARTIFACT_COMPONENTS: Record<
  EnrichedArtifact["type"],
  { Component: FC<any>; propName: string }
> = {
  figure: { Component: FigureMessage, propName: "figure" },
  dataframe: { Component: DataframeMessage, propName: "dataframe" },
  value: { Component: DataValueMessage, propName: "value" },
  text_file: { Component: TextFileMessage, propName: "textFile" },
  blob_file: { Component: BlobFileMessage, propName: "blobFile" },
};

const ArtifactRenderer: FC<{ artifact: EnrichedArtifact }> = ({ artifact }) => {
  const entry = ARTIFACT_COMPONENTS[artifact.type];
  if (!entry) {
    return null;
  }
  const { Component, propName } = entry;
  const data = {
    artifact_id: artifact.artifact_id,
    artifact_name: artifact.artifact_name,
    content: artifact.content,
    summary: artifact.summary,
    code: artifact.code,
  };
  return (
    <div className="px-2">
      <Component {...{ [propName]: data }} debugMode={false} />
    </div>
  );
};

export const FinalResultMessage: FC<any> = (props) => {
  const args = props.args ?? {};

  const content: string = args.content || "";
  const artifactNames: string[] = args.artifacts || [];
  const resolvedArtifacts: EnrichedArtifact[] | undefined = args._resolvedArtifacts;
  const artifactKey = artifactNames.join(",");

  const [enrichedArtifacts, setEnrichedArtifacts] = useState<EnrichedArtifact[]>(
    resolvedArtifacts ?? []
  );
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(false);
  const fetchedRef = useRef<string>("");

  const { conversationId } = useAssistantContext();

  useEffect(() => {
    if (resolvedArtifacts) {
      setEnrichedArtifacts(resolvedArtifacts);
      return;
    }

    if (!artifactKey || !conversationId) {
      return;
    }
    if (fetchedRef.current === artifactKey) {
      return;
    }
    fetchedRef.current = artifactKey;

    let cancelled = false;

    const resolve = async () => {
      setIsLoadingArtifacts(true);
      try {
        const params = new URLSearchParams();
        for (const name of artifactKey.split(",")) {
          params.append("names", name);
        }

        const response = await fetch(
          `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/artifacts/resolve?${params}`
        );

        if (!response.ok) {
          throw new Error(`Failed to resolve artifacts: ${response.status}`);
        }

        const resolved: EnrichedArtifact[] = await response.json();
        if (!cancelled) {
          setEnrichedArtifacts(resolved);
        }
      } catch (error) {
        console.error("Error resolving artifacts:", error);
        fetchedRef.current = "";
      } finally {
        if (!cancelled) {
          setIsLoadingArtifacts(false);
        }
      }
    };

    resolve();

    return () => {
      cancelled = true;
      fetchedRef.current = "";
    };
  }, [artifactKey, conversationId, resolvedArtifacts]);

  const showLoading = isLoadingArtifacts && artifactKey && enrichedArtifacts.length === 0;

  if (!content && enrichedArtifacts.length === 0 && !showLoading) {
    return null;
  }

  return (
    <div className="w-full space-y-4 my-4">
      {content && (
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      )}

      {showLoading && (
        <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span>Loading artifacts…</span>
        </div>
      )}

      {enrichedArtifacts.map((artifact, index) => (
        <ArtifactRenderer key={artifact.artifact_id || `artifact-${index}`} artifact={artifact} />
      ))}
    </div>
  );
};
